/**
 * WsTokenRefresher
 *
 * Listens for 'auth-refresh' events and updates the auth token in all registered
 * WebsocketProvider instances. provider.url is a computed getter built from
 * provider.params, so we mutate provider.params['token'] directly. y-websocket
 * reads params on every reconnect attempt, so the next retry will use the
 * updated token automatically.
 *
 * Only providers whose URL already contains a `token` param are updated. Those
 * created without getWsAuthParams() (no auth requirement) are silently skipped.
 *
 * Registration also forwards the provider to:
 *   - `wsReconnectCoordinator` — tab-visibility-aware reconnect coordination
 *   - `attachHardStop` — kills the y-websocket reconnect loop on first 1008/4503
 *     close (the chandler-pattern fix)
 *   - `attachSizeBudget` — pre-emptively hard-stops if the bound doc grows past
 *     the per-prefix size budget (stops bad payloads before the server sees them)
 *
 * This is the canonical single point for "a new WebsocketProvider now exists" —
 * routing all cross-cutting concerns through it means every existing call site
 * (and every new one) gets the same protection without touching its code.
 *
 * Both safety attachments can be disabled via the
 * `VITE_WS_HARD_STOP_ENABLED=false` env flag — useful for diagnostics, but
 * defaults to ON in production so the chandler-pattern bug stays dead.
 */
import type { WebsocketProvider } from 'y-websocket';
import { wsReconnectCoordinator } from './wsReconnectCoordinator';
import { attachHardStop } from './yjs/wsHardStop';
import { attachSizeBudget, type WsSizeBudgetHandle } from './yjs/wsSizeBudget';

const HARD_STOP_ENABLED = (() => {
  // Default ON. Only the literal string 'false' disables — anything else
  // (including undefined / unset) keeps the safety nets active.
  try {
    return import.meta.env?.VITE_WS_HARD_STOP_ENABLED !== 'false';
  } catch {
    return true;
  }
})();

interface RegistrationHandle {
  hardStopDispose?: () => void;
  sizeBudgetHandle?: WsSizeBudgetHandle;
}

class WsTokenRefresher {
  private readonly providers = new Set<WebsocketProvider>();
  private readonly registrations = new WeakMap<WebsocketProvider, RegistrationHandle>();

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('auth-refresh', () => this.handleTokenRefresh());
    }
  }

  register(provider: WebsocketProvider): void {
    this.providers.add(provider);
    wsReconnectCoordinator.register(provider);

    if (!HARD_STOP_ENABLED) return;

    const handle: RegistrationHandle = {};
    try {
      handle.hardStopDispose = attachHardStop(provider);
    } catch (err) {
      // Defensive — never let a safety attachment break basic provider
      // registration. Logged via console so it surfaces in the frontend log
      // file but doesn't reach Sentry as an exception.
      console.warn('[wsTokenRefresher] attachHardStop failed:', err);
    }

    // Size budget needs the bound Y.Doc. y-websocket exposes it as `.doc`.
    // Skip if absent (would only happen with a non-y-websocket provider —
    // shouldn't reach here, but defensive).
    const doc = (provider as unknown as { doc?: unknown }).doc;
    if (doc && typeof (doc as { on?: unknown }).on === 'function') {
      try {
        handle.sizeBudgetHandle = attachSizeBudget(provider, doc as never);
      } catch (err) {
        console.warn('[wsTokenRefresher] attachSizeBudget failed:', err);
      }
    }

    this.registrations.set(provider, handle);
  }

  unregister(provider: WebsocketProvider): void {
    this.providers.delete(provider);
    wsReconnectCoordinator.unregister(provider);

    const handle = this.registrations.get(provider);
    if (handle) {
      try { handle.hardStopDispose?.(); } catch { /* noop */ }
      try { handle.sizeBudgetHandle?.dispose(); } catch { /* noop */ }
      this.registrations.delete(provider);
    }
  }

  private handleTokenRefresh(): void {
    let newToken: string | null = null;
    try {
      newToken = localStorage.getItem('authToken');
    } catch (_) {}

    if (!newToken) return;

    for (const provider of this.providers) {
      // provider.url is a computed getter from provider.params — mutate params directly.
      // providers without a 'token' param were created without auth and should be skipped.
      if (!('token' in provider.params)) continue;
      provider.params['token'] = newToken;
    }
  }
}

// Preserve the singleton across Vite HMR module re-executions (same pattern as
// wsClientSingleton and wsReconnectCoordinator). Without this, re-executing this
// module adds a duplicate 'auth-refresh' event listener while the preserved
// wsClient's provider registry still points to the old refresher's Set — so
// token updates and hard-stop/size-budget registrations are routed to a stale
// instance that no longer sees any providers.
const _REFRESHER_KEY = Symbol.for('__river_ws_token_refresher__');
const _refresherGlobal = globalThis as unknown as { [_REFRESHER_KEY]?: WsTokenRefresher };
export const wsTokenRefresher: WsTokenRefresher =
  _refresherGlobal[_REFRESHER_KEY] ?? (_refresherGlobal[_REFRESHER_KEY] = new WsTokenRefresher());
